import { createSelector } from 'reselect';
import { List } from 'immutable';
import { ADMIN_PAGE } from './constants';
import { initialState } from './reducer';
import dayjs from 'dayjs';
import {selectCustomers, selectSubscriptions} from "../../redux/selectors/administration.selector";
import { DEVICES } from '../../redux/constants/device.constants';
import { initialState as deviceInitialState } from '../../redux/reducers/device.reducer';
import { mergeV4IntoV3 } from '../../utils/deviceMerge';
import { V4SearchEntity } from '../../utils/v4Search';

const selectState = state => state[ADMIN_PAGE] || initialState;
const selectDeviceState = state => state[DEVICES] || deviceInitialState;
export const selectAccountManagers = () => createSelector(selectState, state => state.get('accountManagers'));
export const selectProms = () => createSelector(selectState, state => state.get('proms'));
export const selectARCs = () => createSelector(selectState, state => state.get('arcs'));
export const selectARCsFetched = () => createSelector(selectState, state => state.get('arcsFetched'));
export const selectAccounts = () => createSelector(selectState, s => s.get('accounts').sort((a, b) => a.get('name').lc() > b.get('name').lc() ? 1:-1));
export const selectEndCustomers = () => createSelector(selectState, s => s.get('accounts')
    .filter(c => !c.deleted && !c.archived && c.isEndCustomer)
    .sort((a, b) => a.get('name').lc() > b.get('name').lc() ? 1:-1));
export const selectActiveAccounts = () => createSelector(selectState, s => s.get('accounts').filter(c => !c.deleted).sort((a,b) => a.get('name').lc() > b.get('name').lc() ? 1:-1));
export const selectBeacons = () => createSelector(selectState, s => s.get('beacons'));
export const selectSites = () => createSelector(selectState, s => s.get('sites'));
export const selectDevices = () => createSelector([selectState, selectDeviceState], (s, deviceState) => {
    // Normalise to a plain array so concat always works consistently
    const raw = s.get('devices');
    let devices = raw && raw.toArray ? raw.toArray() : (Array.isArray(raw) ? raw : []);

    // A search shows its own matches only.
    const searching = s.get('v4Search')?.entity === V4SearchEntity.Devices;
    const v4Devices = (!searching && deviceState.get('v4OnlyDevices')) || List();
    if (v4Devices.size) {
        const v3ByUuid = new Map();
        const v3ById = new Map();
        const v3ByUniqueId = new Map();
        devices.forEach(d => {
            if (d.id) v3ById.set(d.id, d);
            if (d.uuid) v3ByUuid.set(d.uuid, d);
            if (d.uniqueId) v3ByUniqueId.set(d.uniqueId, d);
        });

        const accounts = s.get('accounts');
        const resolveAccountName = (d) => {
            if (d.get('accountId') && !d.get('accountName')) {
                const account = accounts?.find(a => a.get('id') === d.get('accountId'));
                if (account) return d.set('accountName', account.get('name'));
            }
            return d;
        };

        // ─── PREFER-V3-DEDUP (revertable) ───────────────────────────────────
        // Prefer the V3 row on overlap: keep it (its own V3 status/location;
        // only the V4 uuid/owner carried over) and hide the V4 duplicate. Only
        // append V4 devices that have NO matching V3 row. To revert to the V4-preferred behavior,
        // replace the block below with the original:
        //
        //   const v3IdsToDrop = new Set();
        //   const v4ToAppend = [];
        //   v4Devices.toArray().forEach(d => {
        //     const matchedV3 = (d.uuid && v3ByUuid.get(d.uuid)) || (d.id && v3ById.get(d.id)) || (d.uniqueId && v3ByUniqueId.get(d.uniqueId)) || null;
        //     if (!matchedV3) v4ToAppend.push(resolveAccountName(d));
        //     else if (d.isEmerit) { v3IdsToDrop.add(matchedV3.id); v4ToAppend.push(resolveAccountName(d.set('legacyId', matchedV3.id))); }
        //   });
        //   if (v3IdsToDrop.size) devices = devices.filter(d => !v3IdsToDrop.has(d.id));
        //   devices = devices.concat(v4ToAppend);
        // ────────────────────────────────────────────────────────────────────
        const v3Overlays = new Map();
        const v4ToAppend = [];
        v4Devices.toArray().forEach(d => {
            const matchedV3 =
                (d.uuid && v3ByUuid.get(d.uuid)) ||
                (d.id && v3ById.get(d.id)) ||
                (d.uniqueId && v3ByUniqueId.get(d.uniqueId)) ||
                null;
            if (!matchedV3) {
                v4ToAppend.push(resolveAccountName(d));
            } else if (d.isEmerit) {
                v3Overlays.set(matchedV3.id, mergeV4IntoV3(matchedV3, d));
            }
        });

        if (v3Overlays.size) {
            devices = devices.map(d => v3Overlays.get(d.id) || d);
        }
        devices = devices.concat(v4ToAppend);
    }
    return List(devices).sort((a, b) => (a.get('name') || '').lc() > (b.get('name') || '').lc() ? 1 : -1);
});
export const selectSelectedAccount = () => createSelector(selectState, s => s.get('selectedAccount'));
export const selectV4Search = () => createSelector(selectState, s => s.get('v4Search'));
export const selectLayout = () => createSelector(selectState, state => state.get('layout'));
export const selectBillingCycle = () => createSelector(selectState, state => state.get('billingCycle'));
export const selectIsCurrentBillingCycle = () => createSelector(selectState, state => state.get('billingCycle').format('MMYY') === dayjs().format('MMYY'));
export const selectDevicesFetched = () => createSelector(selectState, state => state.get('devicesFetched'));
export const selectAccountsFetched = () => createSelector(selectState, state => state.get('accountsFetched'));
export const selectDeviceFilter = () => createSelector(selectState, state => state.get('deviceFilter'));
export const selectDeviceCounts = () => createSelector(
    [selectState, selectDeviceState],
    (state, deviceState) => {
        const baseCounts = state.get('deviceCounts') || {};
        const v4Devices = deviceState.get('v4OnlyDevices') || List();
        if (!v4Devices.size) return baseCounts;

        const raw = state.get('devices');
        const v3Devices = raw && raw.toArray ? raw.toArray() : (Array.isArray(raw) ? raw : []);
        const v3Uuids = new Set();
        const v3Ids = new Set();
        const v3UniqueIds = new Set();
        v3Devices.forEach(d => {
            const id = d.get?.('id') ?? d.id;
            const uuid = d.get?.('uuid') ?? d.uuid;
            const uniqueId = d.get?.('uniqueId') ?? d.uniqueId;
            if (id) v3Ids.add(id);
            if (uuid) v3Uuids.add(uuid);
            if (uniqueId) v3UniqueIds.add(uniqueId);
        });

        const merged = { ...baseCounts };
        v4Devices.forEach(d => {
            const matched =
                (d.uuid && v3Uuids.has(d.uuid)) ||
                (d.id && v3Ids.has(d.id)) ||
                (d.uniqueId && v3UniqueIds.has(d.uniqueId));
            if (matched) return;
            const accountId = d.get('accountId');
            if (!accountId) return;
            const existing = merged[accountId] || { total: 0, apps: 0 };
            merged[accountId] = {
                total: (existing.total || 0) + 1,
                apps: existing.apps || 0,
            };
        });
        return merged;
    }
);
export const selectShowWiki = () => createSelector(selectState, state => state.get('showWiki'));

export const selectCustomersWithoutAccountManager = () => createSelector([
    selectAccountManagers(),
    selectCustomers(),
    selectSubscriptions()
], (accountManagers, customers, subscriptions) => {
    return customers.filter(c => !accountManagers.find(a => a.customer === c.id) && !!subscriptions.find(s => {
        return  s.customer === c.id && s.status !== 'expired';
    }));
});